import { PERIOD_DAYS, PERIOD_MONTHS, PERIOD_QUARTERS, PERIOD_YEARS } from 'Constants/project_plan';
import moment from 'moment';

/**
 * Translation between the Milestone Roadmap's "Period" date-range control and the existing
 * per-period exclusion map the roadmap endpoint already understands.
 *
 * There is deliberately no backend change: the range is resolved to concrete dates here, the
 * current format's intervals are walked, and every interval that does NOT intersect the range is
 * marked excluded. The resulting map is posted as `periods` (and reused by the PDF export), so the
 * server keeps applying the exclusion logic it always had.
 */

export const PERIOD_RANGE_ALL = 'all';
export const PERIOD_RANGE_NEXT_90_DAYS = 'next-90-days';
export const PERIOD_RANGE_CUSTOM = 'custom';

/** A specific plan year is encoded as `year:2026` so one `<select>` can carry every option. */
export const PERIOD_RANGE_YEAR_PREFIX = 'year:';

export const DEFAULT_PERIOD_RANGE = { preset: PERIOD_RANGE_ALL, from: null, to: null };

const NEXT_90_DAYS_LENGTH = 90;

/**
 * Resolve a `{ preset, from, to }` selection into concrete inclusive bounds.
 *
 * Returns `null` whenever the selection places no bound on the timeline — "All dates", or a custom
 * range that is still half-entered — which callers read as "exclude nothing".
 *
 * @param {{preset: string, from: ?string, to: ?string}} [range]
 * @returns {?{from: moment.Moment, to: moment.Moment}}
 */
export function resolveRange(range) {
    const { preset, from, to } = range || DEFAULT_PERIOD_RANGE;

    if (preset === PERIOD_RANGE_NEXT_90_DAYS) {
        return {
            from: moment().startOf('day'),
            to: moment().add(NEXT_90_DAYS_LENGTH, 'days').endOf('day'),
        };
    }

    if (preset === PERIOD_RANGE_CUSTOM) {
        if (!from || !to) {
            return null;
        }

        return { from: moment(from).startOf('day'), to: moment(to).endOf('day') };
    }

    if (typeof preset === 'string' && preset.startsWith(PERIOD_RANGE_YEAR_PREFIX)) {
        const year = moment(preset.slice(PERIOD_RANGE_YEAR_PREFIX.length), 'YYYY');

        if (!year.isValid()) {
            return null;
        }

        return { from: year.clone().startOf('year'), to: year.clone().endOf('year') };
    }

    return null;
}

/**
 * Flatten the interval tree produced by `getTimeIntervals` into the leaf intervals that become the
 * columns (calendar) / period rows (list) of the given format.
 *
 * The leaves carry their own `key` and `{ start, end }` moments, so keys are never re-derived by
 * string formatting here — the tree is the single source of the key format.
 *
 * @returns {Array<{key: string, start: moment.Moment, end: moment.Moment}>}
 */
export function collectPeriodIntervals(periods, periodType) {
    const intervals = [];

    if (!periods || !periods.years) {
        return intervals;
    }

    Object.entries(periods.years).forEach(([year, yearData]) => {
        if (periodType === PERIOD_YEARS) {
            // The years format keys its columns by the bare year, not by a `key` on the node.
            intervals.push({ key: year, start: yearData.start, end: yearData.end });

            return;
        }

        if (periodType === PERIOD_QUARTERS) {
            Object.values(yearData.quarters || {}).forEach((quarter) => {
                intervals.push({ key: quarter.key, start: quarter.start, end: quarter.end });
            });

            return;
        }

        Object.values(yearData.months || {}).forEach((month) => {
            if (periodType === PERIOD_MONTHS) {
                intervals.push({ key: month.key, start: month.start, end: month.end });

                return;
            }

            if (periodType === PERIOD_DAYS) {
                Object.values(month.days || {}).forEach((day) => {
                    intervals.push({ key: day.key, start: day.start, end: day.end });
                });
            }
        });
    });

    return intervals;
}

/**
 * Keys of every interval of the current format that falls entirely outside the range.
 *
 * A period is kept when it INTERSECTS the range, so a partially-overlapping first/last period still
 * renders rather than being clipped away.
 *
 * @returns {Array<string>} empty when the range places no bound on the timeline
 */
export function buildExcludedPeriodKeys(periods, periodType, range) {
    if (!range || !range.from || !range.to) {
        return [];
    }

    return collectPeriodIntervals(periods, periodType)
        .filter(({ start, end }) => end.isBefore(range.from) || start.isAfter(range.to))
        .map(({ key }) => key);
}

/**
 * Shallow key-set comparison for the two period maps built from the same key list.
 *
 * Both maps hold a constant value per key (`false` for the request payload, `true` for the column
 * exclusions), so comparing the key sets is enough — and it lets callers keep the previous object
 * identity when nothing changed, which is what stops a redundant roadmap refetch.
 */
export function haveSameKeys(a, b) {
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});

    return aKeys.length === bKeys.length && aKeys.every((key) => Object.prototype.hasOwnProperty.call(b || {}, key));
}
